import React, { useState, useEffect } from "react";
import { useRevalidator } from "react-router";
import {
  Card,
  CardContent,
  Stack,
  Typography,
  useTheme,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio,
  Box,
  alpha,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import {
  faCalendarDay,
  faCalendarDays,
  faChevronDown,
  faTrash,
  faXmark,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import CardHeader from "./CardHeader.jsx";
import { longDateTime, fullName } from "../../../utils/formatter.js";
import { useRoles } from "../../../context/AuthenticationTokenContext.jsx";
import { role } from "shared/src/roles.mjs";
import { status } from "shared/src/appointment.mjs";
import * as api from "../../../services/api.js";
import { RESERVATION_MODES } from "../../reservationDialog/ReservationDialog.jsx";
import { faLinkHorizontal } from "@fortawesome/pro-solid-svg-icons";
import { useSnackbarMessage } from "../../Snackbar.jsx";

const MAX_VISIBLE = 2;
const VISIBLE_STATUSES = [status.BOOKED, status.CONFIRMED, status.WAITING];

function canRescheduleAppointment(appointment) {
  const availableProductIds = new Set(
    appointment?.client?.activeTreatments?.map((t) => t.product_id) ?? [],
  );

  const currentProductId = appointment?.service?.product_id;

  return availableProductIds.has(currentProductId);
}

export default function NextAppointmentCard({
  appointments = [],
  client,
  handleOpenReservationDialog,
  cancelAppointment,
  setCancelAppointment,
}) {
  const revalidator = useRevalidator();
  const { t } = useTranslation();
  const theme = useTheme();
  const roles = useRoles();
  const [showAll, setShowAll] = useState(false);
  const [localAppointments, setLocalAppointments] = useState(appointments);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [bulkCancelDialogOpen, setBulkCancelDialogOpen] = useState(false);
  const [selectedCancelationType, setSelectedCancelationType] =
    useState("clinic");
  const [selectedOption, setSelectedOption] = useState("single");
  const [selectedAppt, setSelectedAppt] = useState(null);
  const [cancellationReason, setCancellationReason] = useState("");
  const displaySnackbarMessage = useSnackbarMessage();

  useEffect(() => {
    setLocalAppointments(appointments);
  }, [appointments]);

  useEffect(() => {
    if (!cancelAppointment) return;
    handleOpenCancelDialog(cancelAppointment);
    setCancelAppointment(null);
  }, [cancelAppointment]);

  const now = new Date();
  const isTodayOrFuture = (dateStr) => {
    const d = new Date(dateStr);
    return d.setHours(0, 0, 0, 0) >= now.setHours(0, 0, 0, 0);
  };

  const shouldShowInCard = (appt) => {
    const future = isTodayOrFuture(appt.start);
    const isParent = !appt.parent_id;
    const statusOk = VISIBLE_STATUSES.includes(appt.status_id);
    return future && isParent && statusOk;
  };

  const parentAppointments = localAppointments
    .filter(shouldShowInCard)
    .sort((a, b) => new Date(a.start) - new Date(b.start));

  const visibleAppointments = showAll
    ? parentAppointments
    : parentAppointments.slice(0, MAX_VISIBLE);

  const canManageAppointments =
    roles.has(role.Back_Office) ||
    roles.has(role.Front_Office) ||
    roles.has(role.Manager_Customer_Care);

  const handleOpenCancelDialog = async (appt) => {
    const { data } = await api.loadGroupAppointments({
      filter: {
        and: [
          {
            status_id: {
              in: [status.BOOKED, status.CONFIRMED, status.WAITING],
            },
          },
          {
            or: [
              { group_id: { eq: appt.group_id } },
              { reservation_id: { eq: appt.reservation_id } },
            ],
          },
        ],
      },
    });
    const groupAppointments = data.appointments.data;
    if (groupAppointments.filter((x) => x.parent_id == null).length > 1) {
      setSelectedAppt([appt, groupAppointments]);
      setCancellationReason("");
      setBulkCancelDialogOpen(true);
    } else {
      setSelectedAppt(appt);
      setCancellationReason("");
      setCancelDialogOpen(true);
    }
  };

  const handleBulkConfirmCancel = async () => {
    switch (selectedOption) {
      case "single":
        setSelectedAppt(selectedAppt[0]);
        setCancelDialogOpen(true);
        setBulkCancelDialogOpen(false);
        break;
      case "bulk":
        setSelectedAppt(selectedAppt[1]);
        setBulkCancelDialogOpen(false);
        setCancelDialogOpen(true);
        break;
    }
  };

  const handleConfirmCancel = async () => {
    if (!selectedAppt) return;
    try {
      let apptsToCancel = [];
      let childAppointments = [];

      if (Array.isArray(selectedAppt)) {
        apptsToCancel = [...selectedAppt];
      } else {
        childAppointments = localAppointments.filter(
          (a) => a.parent_id === selectedAppt.id,
        );
        apptsToCancel = [selectedAppt, ...childAppointments];
      }

      await Promise.all(
        apptsToCancel.map((a) =>
          api.patchAppointment({
            id: a.id,
            status_id: status.CANCELED,
            actual_calendar_id: null,
            employee_id: null,
            cancellation_reason: cancellationReason,
            cancellation_type: selectedCancelationType,
          }),
        ),
      );

      revalidator.revalidate();
      displaySnackbarMessage({
        title: "Rezervace byla úspěšně zrušena.",
        content: "",
      });
      setCancelDialogOpen(false);
    } catch (err) {
      console.error("Failed to cancel appointment:", err);
      displaySnackbarMessage({
        severity: "error",
        title: "Chyba při rušení appointmentu.",
        content: "",
      });
    }
  };

  return (
    <>
      <Card
        sx={{ height: "100%", display: "flex", flexDirection: "column" }}
        data-test-id="next-appointment-card"
      >
        <CardContent sx={{ flexGrow: 1 }}>
          <CardHeader
            title={t("nextAppointmentCard.title")}
            color={theme.palette.warning.main}
            faIcon={faCalendarDay}
          />

          <Stack sx={{ mt: -1 }}>
            {visibleAppointments.length > 0 ? (
              visibleAppointments.map((appt) => (
                <Box
                  data-test-id="appointment"
                  key={appt.id}
                  sx={{
                    position: "relative",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 1,
                    mx: -1,
                    px: 1,
                    height: "4rem",
                    ...((appt.group_id || appt.reservation_id) && {
                      "&:hover": {
                        backgroundColor: alpha(theme.palette.primary.main, 0.2),
                        "& .groupAppointment": { display: "block" },
                      },
                    }),
                  }}
                >
                  <Box
                    sx={{
                      mb: 0.5,
                      display: "flex",
                      gap: 1,
                      alignItems: "center",
                      minWidth: 0,
                    }}
                  >
                    <strong
                      style={{
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {`${appt.service?.name || "-"}, ${longDateTime(appt.start)}`}
                    </strong>
                  </Box>

                  {canManageAppointments && (
                    <Box sx={{ display: "flex" }}>
                      {canRescheduleAppointment(appt) && (
                        <IconButton
                          size="small"
                          color="primary"
                          disabled={
                            appt.status_id != status.BOOKED &&
                            appt.status_id != status.CONFIRMED &&
                            appt.status_id != status.WAITING
                          }
                          onClick={() =>
                            handleOpenReservationDialog({
                              appointment: appt,
                              client,
                              mode: RESERVATION_MODES.RESCHEDULE,
                            })
                          }
                          title={t("form.reschedule")}
                        >
                          <FontAwesomeIcon size={"xs"} icon={faCalendarDays} />
                        </IconButton>
                      )}
                      <IconButton
                        size="small"
                        color="primary"
                        disabled={
                          appt.status_id != status.BOOKED &&
                          appt.status_id != status.CONFIRMED &&
                          appt.status_id != status.WAITING
                        }
                        onClick={() => handleOpenCancelDialog(appt)}
                        title={t("form.delete")}
                      >
                        <FontAwesomeIcon size="xs" icon={faTrash} />
                      </IconButton>
                    </Box>
                  )}
                  {appt.group_id && (
                    <Box
                      component="span"
                      className="groupAppointment"
                      sx={{
                        position: "absolute",
                        left: "50%",
                        bottom: 0,
                        color: "primary.dark",
                        display: "none",
                      }}
                    >
                      <FontAwesomeIcon icon={faLinkHorizontal} />
                    </Box>
                  )}
                </Box>
              ))
            ) : (
              <Typography variant="body1" sx={{ mt: 1 }}>
                {t("nextAppointmentCard.noUpcomingAppointments")}
              </Typography>
            )}

            {parentAppointments.length > MAX_VISIBLE && (
              <Box sx={{ display: "flex", justifyContent: "end" }}>
                <Button
                  size="small"
                  onClick={() => setShowAll(!showAll)}
                  sx={{ mt: 1, px: 1 }}
                  endIcon={
                    <FontAwesomeIcon
                      icon={faChevronDown}
                      rotation={showAll ? 180 : 0}
                    />
                  }
                >
                  {showAll
                    ? t("common.showLess", "Zobrazit méně")
                    : t("common.showMore", "Zobrazit více")}
                </Button>
              </Box>
            )}
          </Stack>
        </CardContent>
      </Card>

      <Dialog
        open={cancelDialogOpen}
        onClose={() => setCancelDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Typography
            variant="body3"
            fontWeight="bold"
            color={theme.palette._components?.heading?.color || "text.primary"}
            sx={{
              padding: "1.4rem 2rem",
              margin: 0,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            Zrušení appointmentu
          </Typography>
          <IconButton
            aria-label="close"
            onClick={() => setCancelDialogOpen(false)}
          >
            <FontAwesomeIcon icon={faXmark} fixedWidth />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ padding: "0 4.4rem" }}>
          {selectedAppt && (
            <>
              {!Array.isArray(selectedAppt) && (
                <Typography variant="body2" sx={{ mb: 3 }}>
                  {t("nextAppointmentCard.confirmCancelMessage", {
                    service: selectedAppt.service?.name ?? "-",
                    date: longDateTime(selectedAppt.start),
                  })}
                </Typography>
              )}
              {Array.isArray(selectedAppt) && (
                <Typography variant="body2" sx={{ mb: 3 }}>
                  {t("nextAppointmentCard.confirmCancelMessagePlural")}
                  {selectedAppt
                    .filter((a) => !a.parent_id)
                    .map((a) => (
                      <span key={a.id}>
                        <br />
                        {`${fullName(a.client)} - ${a.service?.name ?? "-"}, ${longDateTime(a.start)}`}
                      </span>
                    ))}
                </Typography>
              )}
              <FormControl>
                <RadioGroup
                  style={{ display: "flex", flexDirection: "row", gap: "2rem" }}
                  defaultValue={"clinic"}
                  onChange={(e) => setSelectedCancelationType(e.target.value)}
                >
                  <FormControlLabel
                    value="clinic"
                    control={<Radio />}
                    label="Zrušila klinika"
                  />
                  <FormControlLabel
                    value="client"
                    control={<Radio />}
                    label="Zrušil klient"
                  />
                </RadioGroup>
              </FormControl>
            </>
          )}

          <TextField
            label={t("nextAppointmentCard.cancellationReason", "Důvod zrušení")}
            fullWidth
            multiline
            value={cancellationReason}
            onChange={(e) => setCancellationReason(e.target.value)}
            sx={{ mt: 2, mb: 3 }}
          />

          {Array.isArray(selectedAppt) &&
            selectedAppt.some((a) => a.parent_id === selectedAppt.id) && (
              <>
                <Typography color="warning.main" sx={{ mb: 1 }}>
                  {t(
                    "nextAppointmentCard.cancelAlsoChildren",
                    "Poznámka: Zruší se i návazné schůzky.",
                  )}
                </Typography>
                {selectedAppt
                  .filter((a) => a.parent_id)
                  .sort((a, b) => new Date(a.start) - new Date(b.start))
                  .map((child) => (
                    <Typography key={child.id} sx={{ color: "text.secondary" }}>
                      •{" "}
                      {`${fullName(child.client)} - ${child.service?.name ?? "-"}, ${longDateTime(child.start)}`}
                    </Typography>
                  ))}
              </>
            )}
          {selectedAppt &&
            !Array.isArray(selectedAppt) &&
            localAppointments.some((a) => a.parent_id === selectedAppt.id) && (
              <>
                <Typography color="warning.main" sx={{ mb: 1 }}>
                  {t(
                    "nextAppointmentCard.cancelAlsoChildren",
                    "Poznámka: Zruší se i návazné schůzky.",
                  )}
                </Typography>
                {localAppointments
                  .filter((a) => a.parent_id === selectedAppt.id)
                  .sort((a, b) => new Date(a.start) - new Date(b.start))
                  .map((child) => (
                    <Typography key={child.id} sx={{ color: "text.secondary" }}>
                      •{" "}
                      {`${child.service?.name || "-"}, ${longDateTime(child.start)}`}
                    </Typography>
                  ))}
              </>
            )}
        </DialogContent>
        <DialogActions sx={{ padding: "3.5rem 4rem 3rem 4rem" }}>
          <Button variant="outlined" onClick={() => setCancelDialogOpen(false)}>
            Zrušit
          </Button>
          {selectedAppt && (
            <Button
              onClick={handleConfirmCancel}
              variant="contained"
              color="error"
              disabled={
                (selectedAppt.status_id === status.CONFIRMED ||
                  selectedAppt.status_id == status.WAITING) &&
                !cancellationReason.trim()
              }
            >
              Potvrdit zrušení
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <Dialog
        open={bulkCancelDialogOpen}
        onClose={() => setBulkCancelDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Typography
            variant="body3"
            fontWeight="bold"
            color={theme.palette._components?.heading?.color || "text.primary"}
            sx={{
              padding: "1.4rem 2rem",
              margin: 0,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            Zrušení appointmentu
          </Typography>
          <IconButton
            aria-label="close"
            onClick={() => setBulkCancelDialogOpen(false)}
          >
            <FontAwesomeIcon icon={faXmark} fixedWidth />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ padding: "0 4.4rem" }}>
          {selectedAppt && (
            <>
              <Typography variant="body2" sx={{ mb: 3 }}>
                Přejete si zrušit pouze tento appointment, nebo celou skupinu?
              </Typography>
              <FormControl>
                <RadioGroup
                  style={{ display: "flex", flexDirection: "row", gap: "2rem" }}
                  defaultValue="single"
                  value={selectedOption}
                  onChange={(e) => setSelectedOption(e.target.value)}
                >
                  <FormControlLabel
                    value="single"
                    control={<Radio />}
                    label="Zrušit pouze tento appointment"
                  />
                  <FormControlLabel
                    value="bulk"
                    control={<Radio />}
                    label="Zrušit celou skupinu"
                  />
                </RadioGroup>
              </FormControl>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ padding: "3.5rem 4rem 3rem 4rem" }}>
          <Button
            variant="outlined"
            onClick={() => setBulkCancelDialogOpen(false)}
          >
            Zrušit
          </Button>
          {selectedAppt && (
            <Button
              onClick={handleBulkConfirmCancel}
              variant="contained"
              color="error"
              disabled={
                (selectedAppt.status_id === status.CONFIRMED ||
                  selectedAppt.status_id == status.WAITING) &&
                !cancellationReason.trim()
              }
            >
              Pokračovat
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
}
